<template>
  <div class="table-content">
    <div class="table-wrap">
      <div class="table-title-div">
        <p class="table-title">Data Quality Scorecard - {{ datasetLabel }}</p>
        <div class="qaqc-scorecard-header-actions">
          <div class="csv-btn" @click="downloadTableCSV">
            <v-img class="pointer" :src="documentDownload" alt="csv download"></v-img>
          </div>
          <v-btn
            icon
            variant="text"
            size="small"
            class="qaqc-scorecard-collapse-btn"
            :aria-label="isCollapsed ? 'Expand' : 'Collapse'"
            @click="isCollapsed = !isCollapsed"
          >
            <v-icon>{{ isCollapsed ? 'mdi-chevron-down' : 'mdi-chevron-up' }}</v-icon>
          </v-btn>
        </div>
      </div>
      <div v-show="!isCollapsed" class="qaqc-table-content">
      <div class="qaqc-scorecard-summary-bar" v-if="summary">
        <div class="qaqc-scorecard-summary-card qaqc-scorecard-summary-overall">
          <div class="qaqc-scorecard-summary-icon-wrap">
            <v-img :src="icons['score']" contain class="qaqc-scorecard-summary-icon-img" />
          </div>
          <span class="qaqc-scorecard-summary-label">Overall Pass Rate</span>
          <span class="qaqc-scorecard-summary-value qaqc-scorecard-summary-value-orange">{{ summary.overallPassRate }}</span>
        </div>
        <div class="qaqc-scorecard-summary-card qaqc-scorecard-summary-passed">
          <div class="qaqc-scorecard-summary-icon-wrap">
            <v-img :src="icons['passed']" contain class="qaqc-scorecard-summary-icon-img" />
          </div>
          <span class="qaqc-scorecard-summary-label">Passed QC</span>
          <span class="qaqc-scorecard-summary-value">{{ summary.passedQc }}</span>
        </div>
        <div class="qaqc-scorecard-summary-card qaqc-scorecard-summary-pending">
          <div class="qaqc-scorecard-summary-icon-wrap">
            <v-img :src="icons['pending']" contain class="qaqc-scorecard-summary-icon-img" />
          </div>
          <span class="qaqc-scorecard-summary-label">Pending QC</span>
          <span class="qaqc-scorecard-summary-value">{{ summary.pendingQc }}</span>
        </div>
        <div class="qaqc-scorecard-summary-card qaqc-scorecard-summary-failed">
          <div class="qaqc-scorecard-summary-icon-wrap">
            <v-img :src="icons['failed']" contain class="qaqc-scorecard-summary-icon-img" />
          </div>
          <span class="qaqc-scorecard-summary-label">Failed</span>
          <span class="qaqc-scorecard-summary-value">{{ summary.failed }}</span>
        </div>
      </div>
      <v-table class="test-data-table qaqc-table-wrap">
        <thead>
          <tr class="main-header">
            <th class="sticky-header-sub">Well</th>
            <th class="sticky-header-sub text-center">Samples</th>
            <th class="sticky-header-sub text-center">Pass Rate</th>
            <th class="sticky-header-sub text-center">Average Quality Score</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, idx) in paginatedRows" :key="idx">
            <td @click="navigateToWellDetail(row.well)">
              <span class="cursor-pointer clickable-cell well-cell no-bg">{{ row.well }}</span>
            </td>
            <td class="text-center">{{ row.samples }}</td>
            <td class="text-center">{{ row.passRate }}</td>
            <td class="text-center">{{ row.avgQualityScore }}</td>
          </tr>
        </tbody>
      </v-table>
      <QAQCPagination
        :total-items="totalItems"
        v-model:current-page="currentPage"
        v-model:items-per-page="itemsPerPage"
        item-label="Wells"
        @update:items-per-page="currentPage = 1"
      />
      </div>
    </div>
  </div>
</template>

<script>
import QAQCPagination from "../QAQCPagination.vue";

export default {
  name: "DataQualityScorecardTable",
  components: { QAQCPagination },
  data() {
    return {
      icons: {
        score: require("@/assets/Svg/score.svg"),
        passed: require("@/assets/Svg/passed.svg"),
        pending: require("@/assets/Svg/pending.svg"),
        failed: require("@/assets/Svg/failed.svg"),
      },
      itemsPerPage: 10,
      currentPage: 1,
      isCollapsed: false,
      documentDownload: require("@/assets/Images/csv.svg"),
    };
  },
  props: {
    rows: { type: Array, default: () => [] },
    summary: {
      type: Object,
      default: () => ({
        overallPassRate: "0%",
        passedQc: 0,
        pendingQc: 0,
        failed: 0,
      }),
    },
    datasetLabel: { type: String, default: "CCE Dataset" },
  },
  computed: {
    totalItems() {
      return (this.rows || []).length;
    },
    startIndex() {
      return (this.currentPage - 1) * this.itemsPerPage;
    },
    endIndex() {
      return Math.min(this.startIndex + this.itemsPerPage, this.totalItems);
    },
    paginatedRows() {
      const list = this.rows || [];
      return list.slice(this.startIndex, this.endIndex);
    },
  },
  watch: {
    rows() {
      this.currentPage = 1;
    },
  },
  methods: {
    navigateToWellDetail(wellName) {
      if (wellName == null || wellName === "" || wellName === "-") return;
      this.$router.push({
        name: "QAQCDetailView",
        query: { type: "wells", well: String(wellName) },
      });
    },
    downloadTableCSV() {
      if (!this.rows || this.rows.length === 0) {
        return;
      }
      const safeLabel = (this.datasetLabel || "dataset").replace(/[^a-z0-9-_]/gi, "_").toLowerCase();
      const fileName = `data_quality_scorecard_${safeLabel}.csv`;
      const csvHeader = ["Well", "Samples", "Pass Rate", "Average Quality Score"];
      const csvRows = this.rows.map((row) => [
        String(row.well ?? ""),
        String(row.samples ?? ""),
        String(row.passRate ?? ""),
        String(row.avgQualityScore ?? ""),
        // String(row.passFailPerformance ?? ""),
      ]);
      const csvContent = [csvHeader.join(","), ...csvRows.map((row) => row.join(","))].join("\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute("href", url);
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }
    },
  },
};
</script>
